/**
 * User Settings body — Slack (+ voice), Jira, GitHub, AI generation, Appearance.
 * Used inside SettingsModal; subsection actions stay contextual.
 */

import React, { useState } from 'react';
import { ErrorState } from '../../ui/common/ErrorState';
import { CardSkeleton } from '../../ui/common/CardSkeleton';
import { useTheme } from '../../theme';
import { SettingsAiSection } from './SettingsAiSection';
import { SettingsAlertsSection } from './SettingsAlertsSection';
import { SettingsAppearanceSection } from './SettingsAppearanceSection';
import { SettingsKeyboardShortcutsSection } from './SettingsKeyboardShortcutsSection';
import { SettingsJiraSection } from './SettingsJiraSection';
import { SettingsGitHubSection } from './SettingsGitHubSection';
import { SettingsGmailSection } from './SettingsGmailSection';
import { SettingsSlackSection } from './SettingsSlackSection';
import {
  providerHeaderStatus,
  type ProviderHeaderStatus,
  type ProviderHeaderTone,
} from './settingsHelpers';
import { ProviderIcon, type ProviderIconName } from './ProviderIcon';
import { ConnectionTestLabel } from './ConnectionTestLabel';
import { SettingsChangelogSection } from './SettingsChangelogSection';
import { SettingsPeopleSection } from './SettingsPeopleSection';
import { useSettingsController } from './useSettingsController';
import { savedChipStyle } from './settingsStyles';
import { providerOAuthClient } from '../../services/clients';
import type { UserSettingsPublic } from '../../services/clients/types';
import { isConnectableSource, type ConnectableSource } from '../../domain/providerIds';
import { SETTINGS_PROVIDER_ORDER, SOURCE_PROVIDERS } from '../../domain/providerRegistry';
import { PROVIDER_PROBES } from '../../services/providerProbes';
import { AI_PROVIDER_LABELS } from './aiSettingsLabels';

export type SettingsSourceId =
  'slack' | 'jira' | 'github' | 'gmail' | 'openai-codex' | 'gemini' | 'opencode-go';
export type SettingsSectionId =
  | 'providers'
  | 'sources'
  | SettingsSourceId
  | 'ai'
  | 'alerts'
  | 'appearance'
  | 'shortcuts'
  | 'changelog'
  | 'people';

export interface SettingsViewProps {
  section?: SettingsSectionId;
  source?: SettingsSourceId | null;
  onSourceChange?: (source: SettingsSourceId | null) => void;
  embedded?: boolean;
  userId?: string;
}

function SettingsStatusBanners({
  error,
  statusMessage,
}: {
  error: string | null;
  statusMessage: string | null;
}) {
  return (
    <>
      {error ? (
        <div
          role="alert"
          style={{
            marginBottom: 12,
            padding: '10px 12px',
            borderRadius: 8,
            background: 'var(--z-bad-soft, #fee2e2)',
            color: 'var(--z-bad, #b91c1c)',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      ) : null}
      {statusMessage ? (
        <div
          data-testid="settings-status"
          style={{
            marginBottom: 12,
            padding: '10px 12px',
            borderRadius: 8,
            background: 'var(--z-ok-soft, #dcfce7)',
            color: 'var(--z-ok, #166534)',
            fontSize: 13,
          }}
        >
          {statusMessage}
        </div>
      ) : null}
    </>
  );
}

function SettingsSavedHint({
  embedded,
  savedHint,
}: {
  embedded: boolean;
  savedHint: string | null;
}) {
  if (!savedHint) return null;
  if (embedded) {
    return (
      <div style={{ marginBottom: 8 }}>
        <span data-testid="settings-saved-hint" style={savedChipStyle} aria-live="polite">
          {savedHint}
        </span>
      </div>
    );
  }
  return (
    <span data-testid="settings-saved-hint" style={savedChipStyle} aria-live="polite">
      {savedHint}
    </span>
  );
}

type AiProviderId = Exclude<SettingsSourceId, 'slack' | 'jira' | 'github' | 'gmail'>;
type SecretStatus = UserSettingsPublic['secrets']['geminiApiKey'];
type ProbeResult = { status: string; message: string };
type SavePatchFn = ReturnType<typeof useSettingsController>['savePatch'];

const AI_PROVIDER_DOCUMENTATION: Record<AiProviderId, string> = {
  'openai-codex': 'https://platform.openai.com/api-keys',
  gemini: 'https://aistudio.google.com/app/apikey',
  'opencode-go': 'https://opencode.ai/docs/providers',
};

function aiProviderSecret(provider: AiProviderId, settings: UserSettingsPublic): SecretStatus {
  if (provider === 'gemini') return settings.secrets.geminiApiKey;
  if (provider === 'opencode-go') return settings.secrets.opencodeGoApiKey;
  return settings.secrets.openAiApiKey;
}

const buttonStyle = {
  padding: '9px 12px',
  borderRadius: 8,
  border: 0,
  background: 'var(--z-accent)',
  color: '#fff',
  cursor: 'pointer',
} as const;
const outlineButtonStyle = {
  padding: '9px 12px',
  borderRadius: 8,
  border: '1px solid var(--z-border)',
  background: 'transparent',
  color: 'var(--z-text)',
  cursor: 'pointer',
} as const;
const draftInputStyle = {
  padding: 10,
  borderRadius: 8,
  border: '1px solid var(--z-border)',
  background: 'var(--z-surface)',
  color: 'var(--z-text)',
} as const;

function AiProviderStatusLine({ secret }: { secret: SecretStatus }) {
  return (
    <div style={{ color: 'var(--z-dim)', fontSize: 13 }}>
      Estado:{' '}
      <strong style={{ color: 'var(--z-text)' }}>
        {secret?.configured
          ? `Conectado${secret.lastFour ? ` · termina en ${secret.lastFour}` : ''}`
          : 'No conectado'}
      </strong>
    </div>
  );
}

function AiProviderProbeResult({
  provider,
  probe,
}: {
  provider: AiProviderId;
  probe: ProbeResult | null;
}) {
  if (!probe) return null;
  return (
    <p
      data-testid={`settings-ai-probe-${provider}`}
      style={{
        margin: 0,
        color:
          probe.status === 'ok'
            ? 'var(--z-ok)'
            : probe.status === 'not_configured'
              ? 'var(--z-text-2)'
              : 'var(--z-danger)',
        fontSize: 13,
      }}
    >
      {probe.message}
    </p>
  );
}

function AiApiKeyControls({
  provider,
  label,
  secret,
  saving,
  onSavePatch,
  onProbe,
  probeBusy,
}: {
  provider: AiProviderId;
  label: string;
  secret: SecretStatus;
  saving: boolean;
  onSavePatch: SavePatchFn;
  onProbe: (run: Promise<{ data: ProbeResult }>) => void;
  probeBusy: boolean;
}) {
  const [draft, setDraft] = useState('');
  const savePatch =
    provider === 'gemini'
      ? (value: string) => ({ geminiApiKey: value })
      : provider === 'opencode-go'
        ? (value: string) => ({ opencodeGoApiKey: value })
        : (value: string) => ({ openAiApiKey: value });
  const clearPatch =
    provider === 'gemini'
      ? { clearGeminiApiKey: true }
      : provider === 'opencode-go'
        ? { clearOpencodeGoApiKey: true }
        : { clearOpenAiApiKey: true };
  return (
    <>
      <input
        type="password"
        autoComplete="off"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder={`Pega tu API key de ${label}`}
        style={draftInputStyle}
      />
      <a
        href={AI_PROVIDER_DOCUMENTATION[provider]}
        target="_blank"
        rel="noreferrer"
        style={{ alignSelf: 'flex-start', color: 'var(--z-accent)', fontSize: 12 }}
      >
        ¿Dónde obtengo mi API key?
      </a>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          disabled={!draft.trim() || saving}
          onClick={() =>
            void onSavePatch(savePatch(draft.trim()), `API key de ${label} actualizada`, () =>
              setDraft(''),
            )
          }
          style={buttonStyle}
        >
          Guardar API key
        </button>
        {secret?.configured ? (
          <button
            type="button"
            disabled={saving}
            onClick={() => void onSavePatch(clearPatch, `API key de ${label} eliminada`)}
            style={outlineButtonStyle}
          >
            Eliminar API key
          </button>
        ) : null}
        <button
          type="button"
          disabled={saving || probeBusy || !secret?.configured}
          onClick={() =>
            onProbe(
              providerOAuthClient.testConnection(provider === 'openai-codex' ? 'openai' : provider),
            )
          }
          style={outlineButtonStyle}
        >
          {probeBusy ? 'Probando…' : 'Probar conexión'}
        </button>
      </div>
    </>
  );
}

function AiProviderSettingsContent({
  provider,
  settings,
  saving,
  onSavePatch,
  inAccordion = false,
}: {
  provider: AiProviderId;
  settings: UserSettingsPublic;
  saving: boolean;
  onSavePatch: SavePatchFn;
  inAccordion?: boolean;
}) {
  const [probe, setProbe] = useState<ProbeResult | null>(null);
  const [probeBusy, setProbeBusy] = useState(false);
  const secret = aiProviderSecret(provider, settings);
  const label = AI_PROVIDER_LABELS[provider];

  const runProbe = (request: Promise<{ data: ProbeResult }>) => {
    setProbeBusy(true);
    void request
      .then(({ data }) => setProbe(data))
      .catch((error: Error) => setProbe({ status: 'error', message: error.message }))
      .finally(() => setProbeBusy(false));
  };

  return (
    <section data-testid={`settings-ai-provider-${provider}`} style={{ display: 'grid', gap: 14 }}>
      {!inAccordion ? (
        <>
          <div>
            <h2 style={{ margin: 0, color: 'var(--z-text)', fontSize: 18 }}>{label}</h2>
          </div>
          <AiProviderStatusLine secret={secret} />
        </>
      ) : (
        <p style={{ ...savedChipStyle, color: 'var(--z-dim)', fontWeight: 400, margin: 0 }}>
          Usa una API key para conectar {label}.
        </p>
      )}
      <AiApiKeyControls
        provider={provider}
        label={label}
        secret={secret}
        saving={saving}
        onSavePatch={onSavePatch}
        onProbe={runProbe}
        probeBusy={probeBusy}
      />
      <AiProviderProbeResult provider={provider} probe={probe} />
    </section>
  );
}

interface SettingsVisibility {
  showSlack: boolean;
  showJira: boolean;
  showGitHub: boolean;
  showAi: boolean;
  showAlerts: boolean;
  showAppearance: boolean;
  showShortcuts: boolean;
  showSourcesGrid: boolean;
  showPeople: boolean;
}

function computeSettingsVisibility(
  embedded: boolean,
  section: SettingsSectionId,
): SettingsVisibility {
  // Embedded Settings never open a provider "detail" page: a deep link with a
  // source expands that provider's accordion at the Providers root instead.
  const atProvidersRoot = embedded && (section === 'providers' || section === 'sources');
  return {
    showSlack: !embedded || section === 'slack',
    showJira: !embedded || section === 'jira',
    showGitHub: !embedded || section === 'github',
    showAi: !embedded || section === 'ai',
    showAlerts: !embedded || section === 'alerts',
    showAppearance: !embedded || section === 'appearance',
    showShortcuts: !embedded || section === 'shortcuts',
    showSourcesGrid: atProvidersRoot,
    showPeople: !embedded || section === 'people',
  };
}

/** Derived from `SOURCE_PROVIDERS` (see `providerRegistry.ts`) in `SETTINGS_PROVIDER_ORDER`. */
const WORK_SOURCE_CARDS = SETTINGS_PROVIDER_ORDER.map(
  (id) =>
    [
      id,
      SOURCE_PROVIDERS[id].settingsCard.label,
      SOURCE_PROVIDERS[id].settingsCard.description,
    ] as const,
);

const AI_PROVIDER_CARDS = [
  ['openai-codex', 'OpenAI / Codex'],
  ['gemini', 'Gemini'],
  ['opencode-go', 'OpenCode Go'],
] as const;

const HEADER_TONE_COLOR: Record<ProviderHeaderTone, string> = {
  ok: 'var(--z-ok, #16803c)',
  warn: 'var(--z-warn, #b45309)',
  danger: 'var(--z-danger, #b91c1c)',
  muted: 'var(--z-dim)',
};

function SettingsProviderCard({
  icon,
  status,
  headerAction,
  label,
  testId,
  expanded,
  onToggle,
  children,
}: {
  id: SettingsSourceId;
  label: string;
  description: string;
  icon?: ProviderIconName;
  status?: ProviderHeaderStatus;
  headerAction?: React.ReactNode;
  testId: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--z-border)',
        borderRadius: 12,
        background: 'var(--z-surface)',
        color: 'var(--z-text)',
        overflow: 'hidden',
      }}
    >
      <div
        className="z-settings-provider-header"
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px' }}
      >
        <button
          type="button"
          aria-expanded={expanded}
          data-testid={testId}
          onClick={onToggle}
          style={{
            display: 'flex',
            flex: 1,
            alignItems: 'center',
            gap: 12,
            textAlign: 'left',
            padding: 0,
            border: 0,
            background: 'transparent',
            color: 'inherit',
            cursor: 'pointer',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            {icon ? <ProviderIcon name={icon} /> : null}
            <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <span style={{ fontWeight: 700 }}>{label}</span>
              {status ? (
                <span
                  data-testid={`${testId}-status`}
                  data-tone={status.tone}
                  style={{
                    color: HEADER_TONE_COLOR[status.tone],
                    fontSize: 12,
                    fontWeight: status.tone === 'ok' || status.tone === 'muted' ? 500 : 700,
                  }}
                >
                  <span aria-hidden="true">•</span> {status.label}
                </span>
              ) : null}
            </span>
          </span>
        </button>
        {headerAction}
        <button
          type="button"
          aria-label={expanded ? `Contraer ${label}` : `Expandir ${label}`}
          onClick={onToggle}
          style={{
            border: 0,
            padding: 4,
            background: 'transparent',
            color: 'var(--z-dim)',
            cursor: 'pointer',
            fontSize: 18,
            lineHeight: 1,
          }}
        >
          {expanded ? '−' : '+'}
        </button>
      </div>
      {expanded ? (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--z-border)' }}>
          {children}
        </div>
      ) : null}
    </div>
  );
}

function ProviderProbeButton({
  provider,
  enabled,
}: {
  provider: SettingsSourceId;
  enabled: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const runProbe = () => {
    setBusy(true);
    setMessage(null);
    const request = isConnectableSource(provider)
      ? PROVIDER_PROBES[provider]()
      : providerOAuthClient.testConnection(provider === 'openai-codex' ? 'openai' : provider);
    void request
      .then(({ data }) => {
        const result = data as { status?: string; message?: string; error?: string };
        setMessage(result.status === 'error' ? 'No verificado' : 'Verificado');
      })
      .catch((error: Error) => setMessage(error.message))
      .finally(() => setBusy(false));
  };
  return (
    <button
      type="button"
      data-testid={`settings-${provider}-test-connection`}
      disabled={busy || !enabled}
      title={message ?? undefined}
      onClick={runProbe}
      className="z-btn z-btn-ghost z-btn-sm"
      style={{ alignSelf: 'center', whiteSpace: 'nowrap', display: 'inline-flex', gap: 6 }}
    >
      <ConnectionTestLabel>
        {busy ? 'Probando…' : (message ?? 'Probar conexión')}
      </ConnectionTestLabel>
    </button>
  );
}

function providerCanTest(settings: UserSettingsPublic | null, id: SettingsSourceId) {
  if (!settings) return false;
  if (id === 'slack') return settings.secrets.slackUserToken.configured;
  if (id === 'github') return settings.secrets.githubPersonalAccessToken.configured;
  if (id === 'gmail') return settings.gmail.connected;
  if (id === 'gemini') return Boolean(settings.secrets.geminiApiKey?.configured);
  if (id === 'opencode-go') return settings.secrets.opencodeGoApiKey.configured;
  if (id === 'openai-codex') return Boolean(settings.secrets.openAiApiKey?.configured);
  return (
    settings.secrets.jiraApiToken.configured &&
    Boolean(settings.jira.baseUrl.trim()) &&
    Boolean(settings.jira.email.trim())
  );
}

function sourceSummary(settings: UserSettingsPublic, id: ConnectableSource) {
  return settings.providers?.sources.find((entry) => entry.provider === id) ?? null;
}

function providerCardStatus(
  settings: ReturnType<typeof useSettingsController>['settings'],
  id: SettingsSourceId,
): ProviderHeaderStatus {
  if (!settings) return { label: 'No configurado', ok: false, tone: 'muted' };
  if (id === 'slack') {
    return providerHeaderStatus(
      settings.secrets.slackUserToken.configured,
      sourceSummary(settings, 'slack'),
    );
  }
  if (id === 'github') {
    return providerHeaderStatus(
      settings.secrets.githubPersonalAccessToken.configured,
      sourceSummary(settings, 'github'),
    );
  }
  if (id === 'gmail') {
    return providerHeaderStatus(settings.gmail.connected, sourceSummary(settings, 'gmail'));
  }
  if (id === 'gemini') {
    return providerHeaderStatus(Boolean(settings.secrets.geminiApiKey?.configured), null);
  }
  if (id === 'opencode-go') {
    return providerHeaderStatus(settings.secrets.opencodeGoApiKey.configured, null);
  }
  if (id === 'openai-codex') {
    return providerHeaderStatus(Boolean(settings.secrets.openAiApiKey?.configured), null);
  }
  const configured =
    (settings.secrets.jiraApiToken.configured &&
      Boolean(settings.jira.baseUrl.trim()) &&
      Boolean(settings.jira.email.trim())) ||
    settings.jira.oauthConnected;
  return providerHeaderStatus(configured, sourceSummary(settings, 'jira'));
}

function SettingsSourcesGrid({
  ctrl,
  expandedSource: controlledExpanded,
  onExpandedChange,
}: {
  ctrl: ReturnType<typeof useSettingsController>;
  /** Controlled expansion (deep links / OAuth returns). Uncontrolled when undefined. */
  expandedSource?: SettingsSourceId | null;
  onExpandedChange?: (source: SettingsSourceId | null) => void;
}) {
  const [localExpanded, setLocalExpanded] = useState<SettingsSourceId | null>(
    controlledExpanded ?? null,
  );
  const expandedSource = onExpandedChange ? (controlledExpanded ?? null) : localExpanded;
  const setExpandedSource = (
    next: (current: SettingsSourceId | null) => SettingsSourceId | null,
  ) => {
    const value = next(expandedSource);
    setLocalExpanded(value);
    onExpandedChange?.(value);
  };
  const renderProvider = (id: SettingsSourceId) => (
    <SettingsProviderEditor id={id} ctrl={ctrl} inAccordion />
  );
  return (
    <section aria-labelledby="settings-sources-title" style={{ display: 'grid', gap: 12 }}>
      <div>
        <h2 id="settings-sources-title" style={{ margin: 0, color: 'var(--z-text)', fontSize: 18 }}>
          Proveedores
        </h2>
        <p style={{ ...savedChipStyle, color: 'var(--z-dim)', fontWeight: 400, margin: '6px 0 0' }}>
          Conecta tus fuentes de trabajo y proveedores de IA desde un mismo lugar.
        </p>
      </div>
      {WORK_SOURCE_CARDS.map(([id, label, description]) => (
        <SettingsProviderCard
          key={id}
          id={id}
          label={label}
          description={description}
          icon={id}
          status={providerCardStatus(ctrl.settings, id)}
          headerAction={
            <ProviderProbeButton provider={id} enabled={providerCanTest(ctrl.settings, id)} />
          }
          testId={`settings-source-provider-card-${id}`}
          expanded={expandedSource === id}
          onToggle={() => setExpandedSource((current) => (current === id ? null : id))}
        >
          {renderProvider(id)}
        </SettingsProviderCard>
      ))}
      <div style={{ color: 'var(--z-text-2)', fontSize: 12, fontWeight: 700, marginTop: 8 }}>
        Proveedores de IA
      </div>
      {AI_PROVIDER_CARDS.map(([id, label]) => (
        <SettingsProviderCard
          key={id}
          id={id}
          label={label}
          description=""
          icon={id}
          status={providerCardStatus(ctrl.settings, id)}
          headerAction={
            <ProviderProbeButton provider={id} enabled={providerCanTest(ctrl.settings, id)} />
          }
          testId={`settings-ai-provider-card-${id}`}
          expanded={expandedSource === id}
          onToggle={() => setExpandedSource((current) => (current === id ? null : id))}
        >
          {renderProvider(id)}
        </SettingsProviderCard>
      ))}
    </section>
  );
}

function SettingsProviderEditor({
  id,
  ctrl,
  inAccordion = false,
}: {
  id: SettingsSourceId;
  ctrl: ReturnType<typeof useSettingsController>;
  inAccordion?: boolean;
}) {
  if (!ctrl.settings) return null;
  if (id === 'slack')
    return (
      <SettingsSlackSection
        settings={ctrl.settings}
        saving={ctrl.saving}
        slackTokenDraft={ctrl.slackTokenDraft}
        voiceDraft={ctrl.voiceDraft}
        savedVoicePrompt={ctrl.savedVoicePrompt}
        onSlackTokenDraft={ctrl.setSlackTokenDraft}
        onVoiceDraft={ctrl.setVoiceDraftSynced}
        onAutoSaveVoiceSelects={ctrl.autoSaveVoiceSelects}
        onSavePatch={ctrl.savePatch}
        onCancelNotes={ctrl.cancelNotes}
      />
    );
  if (id === 'jira')
    return (
      <SettingsJiraSection
        inAccordion={inAccordion}
        settings={ctrl.settings}
        saving={ctrl.saving}
        jiraDraft={ctrl.jiraDraft}
        jiraTokenDraft={ctrl.jiraTokenDraft}
        onJiraDraft={ctrl.setJiraDraft}
        onJiraTokenDraft={ctrl.setJiraTokenDraft}
        onAutoSaveJiraConnection={ctrl.autoSaveJiraConnection}
        onSavePatch={ctrl.savePatch}
      />
    );
  if (id === 'github')
    return (
      <SettingsGitHubSection
        inAccordion={inAccordion}
        settings={ctrl.settings}
        saving={ctrl.saving}
        githubTokenDraft={ctrl.githubTokenDraft}
        onGithubTokenDraft={ctrl.setGithubTokenDraft}
        onSavePatch={ctrl.savePatch}
      />
    );
  if (id === 'gmail')
    return (
      <SettingsGmailSection
        inAccordion={inAccordion}
        settings={ctrl.settings}
        saving={ctrl.saving}
        onSavePatch={ctrl.savePatch}
        onSettingsRefresh={ctrl.applyExternalSettings}
      />
    );
  return (
    <AiProviderSettings
      provider={id as AiProviderId}
      settings={ctrl.settings}
      saving={ctrl.saving}
      onSavePatch={ctrl.savePatch}
      inAccordion={inAccordion}
    />
  );
}

export function AiProviderSettings(props: React.ComponentProps<typeof AiProviderSettingsContent>) {
  return <AiProviderSettingsContent {...props} />;
}

export const SettingsView: React.FC<SettingsViewProps> = (props) => {
  const ctrl = useSettingsController();
  if (ctrl.loading && !ctrl.settings)
    return (
      <div data-testid="settings-view">
        <CardSkeleton />
      </div>
    );
  if (ctrl.error && !ctrl.settings)
    return (
      <div data-testid="settings-view">
        <ErrorState message={ctrl.error} onRetry={() => void ctrl.load()} />
      </div>
    );
  return <SettingsViewContent {...props} ctrl={ctrl} />;
};

function SettingsViewContent({
  section = 'sources',
  source = null,
  onSourceChange,
  embedded = false,
  userId,
  ctrl,
}: SettingsViewProps & { ctrl: ReturnType<typeof useSettingsController> }) {
  const { theme, setTheme } = useTheme();

  const visibility = computeSettingsVisibility(embedded, section);

  return (
    <div data-testid="settings-view" style={{ maxWidth: embedded ? '100%' : 640 }}>
      {!embedded ? (
        <header style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--z-text)' }}>
              Settings
            </h1>
            <SettingsSavedHint embedded={false} savedHint={ctrl.savedHint} />
          </div>
        </header>
      ) : (
        <SettingsSavedHint embedded savedHint={ctrl.savedHint} />
      )}

      <SettingsStatusBanners error={ctrl.error} statusMessage={ctrl.statusMessage} />

      {visibility.showPeople && section === 'people' ? <SettingsPeopleSection /> : null}

      {visibility.showSourcesGrid ? (
        <SettingsSourcesGrid
          ctrl={ctrl}
          expandedSource={source}
          onExpandedChange={onSourceChange}
        />
      ) : null}

      <SettingsIntegrationSections
        ctrl={ctrl}
        showSlack={visibility.showSlack}
        showJira={visibility.showJira}
        showGitHub={visibility.showGitHub}
        showAi={visibility.showAi}
        showAlerts={visibility.showAlerts}
      />
      <SettingsPreferenceSections
        showAppearance={visibility.showAppearance}
        showShortcuts={visibility.showShortcuts}
        embedded={embedded}
        section={section}
        theme={theme}
        setTheme={setTheme}
        userId={userId}
      />
    </div>
  );
}

function SettingsIntegrationSections({
  ctrl,
  showSlack,
  showJira,
  showGitHub,
  showAi,
  showAlerts,
}: {
  ctrl: ReturnType<typeof useSettingsController>;
  showSlack: boolean;
  showJira: boolean;
  showGitHub: boolean;
  showAi: boolean;
  showAlerts: boolean;
}) {
  if (!ctrl.settings) return null;
  return (
    <>
      {showAlerts ? (
        <SettingsAlertsSection
          settings={ctrl.settings}
          saving={ctrl.saving}
          onSavePatch={ctrl.savePatch}
        />
      ) : null}
      {showSlack ? (
        <SettingsSlackSection
          settings={ctrl.settings}
          saving={ctrl.saving}
          slackTokenDraft={ctrl.slackTokenDraft}
          voiceDraft={ctrl.voiceDraft}
          savedVoicePrompt={ctrl.savedVoicePrompt}
          onSlackTokenDraft={ctrl.setSlackTokenDraft}
          onVoiceDraft={ctrl.setVoiceDraftSynced}
          onAutoSaveVoiceSelects={ctrl.autoSaveVoiceSelects}
          onSavePatch={ctrl.savePatch}
          onCancelNotes={ctrl.cancelNotes}
        />
      ) : null}
      {showJira ? (
        <SettingsJiraSection
          settings={ctrl.settings}
          saving={ctrl.saving}
          jiraDraft={ctrl.jiraDraft}
          jiraTokenDraft={ctrl.jiraTokenDraft}
          onJiraDraft={ctrl.setJiraDraft}
          onJiraTokenDraft={ctrl.setJiraTokenDraft}
          onAutoSaveJiraConnection={ctrl.autoSaveJiraConnection}
          onSavePatch={ctrl.savePatch}
        />
      ) : null}
      {showGitHub ? (
        <SettingsGitHubSection
          settings={ctrl.settings}
          saving={ctrl.saving}
          githubTokenDraft={ctrl.githubTokenDraft}
          onGithubTokenDraft={ctrl.setGithubTokenDraft}
          onSavePatch={ctrl.savePatch}
        />
      ) : null}
      {showAi ? (
        <SettingsAiSection
          settings={ctrl.settings}
          saving={ctrl.saving}
          aiDraft={ctrl.aiDraft}
          onAiDraft={ctrl.setAiDraft}
          onAutoSaveAi={ctrl.autoSaveAi}
          onSavePatch={ctrl.savePatch}
        />
      ) : null}
    </>
  );
}

function SettingsPreferenceSections({
  showAppearance,
  showShortcuts,
  embedded,
  section,
  theme,
  setTheme,
  userId,
}: {
  showAppearance: boolean;
  showShortcuts: boolean;
  embedded: boolean;
  section: SettingsSectionId;
  theme: ReturnType<typeof useTheme>['theme'];
  setTheme: ReturnType<typeof useTheme>['setTheme'];
  userId?: string;
}) {
  return (
    <>
      {showAppearance ? <SettingsAppearanceSection theme={theme} onThemeChange={setTheme} /> : null}
      {showShortcuts ? <SettingsKeyboardShortcutsSection userId={userId} /> : null}
      {embedded && section === 'changelog' ? <SettingsChangelogSection /> : null}
    </>
  );
}
